import {
  MutableRefObject,
  SyntheticEvent,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// eslint-disable-next-line import/no-extraneous-dependencies
import { OnChangeHandlerFunc } from "react-mentions";

import { UseMentionsInputParams } from "./MentionsInput.types";
import { findMatchingBracket } from "./MentionsInput.utils";

export const BRACKET_HIGHLIGHT_NAME = "mentions-input-bracket-match";

// CSS Custom Highlight API chưa có trong lib.dom của TypeScript 5.0
type HighlightInstance = object;
type HighlightConstructor = new (...ranges: Range[]) => HighlightInstance;
type HighlightRegistry = {
  get: (name: string) => HighlightInstance | undefined;
  set: (name: string, highlight: HighlightInstance) => void;
  delete: (name: string) => boolean;
};

const getHighlightApi = () => {
  const HighlightCtor = (globalThis as { Highlight?: HighlightConstructor })
    .Highlight;
  const registry =
    typeof CSS !== "undefined"
      ? (CSS as unknown as { highlights?: HighlightRegistry }).highlights
      : undefined;
  return HighlightCtor && registry ? { HighlightCtor, registry } : null;
};

const createTextRange = (
  container: HTMLElement,
  plainTextIndex: number
): Range | null => {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let offset = 0;
  let node = walker.nextNode();
  while (node) {
    const length = node.textContent?.length ?? 0;
    if (plainTextIndex < offset + length) {
      const range = document.createRange();
      range.setStart(node, plainTextIndex - offset);
      range.setEnd(node, plainTextIndex - offset + 1);
      return range;
    }
    offset += length;
    node = walker.nextNode();
  }
  return null;
};

export const useMentionsInput = <R>({
  ref,
  defaultValue,
  value,
  onChange,
}: UseMentionsInputParams<R>) => {
  const mentionsInputRef = useRef(null);
  const [state, setState] = useState<string>(
    (value ?? defaultValue ?? "") as unknown as string
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    (() => {
      return {
        instance: mentionsInputRef.current,
        value: state,
        setValue: (v: string) => {
          setState(v);
        },
        reset: () => {
          setState("");
        },
      };
    }) as unknown as () => R,
    [state]
  );

  const onChangeHandler: OnChangeHandlerFunc = (
    _event: { target: { value: string } },
    newValue: string
  ) => {
    if (typeof value === "undefined") {
      setState(newValue);
    }
    onChange?.(newValue);
  };

  const onClearStateHandler = () => {
    if (typeof value === "undefined") {
      setState("");
    }
    onChange?.("");
  };

  const valueState = useMemo(
    () => (typeof value !== "undefined" ? value : state),
    [value, state]
  );

  return {
    mentionsInputRef,
    valueState,
    onChangeHandler,
    onClearStateHandler,
  };
};

export const useFormulaBracketHighlight = ({
  enabled,
  value,
  mentionsInputRef,
}: {
  enabled: boolean;
  value: string;
  mentionsInputRef: MutableRefObject<unknown>;
}) => {
  const [selection, setSelection] = useState<{
    start: number;
    end: number;
  } | null>(null);

  const onSelect = useCallback((event: SyntheticEvent) => {
    const { selectionStart, selectionEnd } =
      event.target as HTMLTextAreaElement;
    setSelection({ start: selectionStart, end: selectionEnd });
  }, []);

  const onBlur = useCallback(() => setSelection(null), []);

  useLayoutEffect(() => {
    const api = getHighlightApi();
    if (!enabled || !selection || !api) return undefined;

    const index = findMatchingBracket(value, selection.start, selection.end);
    const highlighter = (
      mentionsInputRef.current as { highlighterElement?: HTMLElement } | null
    )?.highlighterElement;
    if (index === null || !highlighter) return undefined;

    const range = createTextRange(highlighter, index);
    if (!range) return undefined;

    const highlight = new api.HighlightCtor(range);
    api.registry.set(BRACKET_HIGHLIGHT_NAME, highlight);
    return () => {
      if (api.registry.get(BRACKET_HIGHLIGHT_NAME) === highlight) {
        api.registry.delete(BRACKET_HIGHLIGHT_NAME);
      }
    };
  }, [enabled, value, selection, mentionsInputRef]);

  return { onSelect, onBlur };
};
